import React from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Send, ExternalLink, Save, Link2, BarChart3, TrendingUp, TrendingDown, MessageCircle, Sparkles, ClipboardCopy } from "lucide-react";

/**
 * Substack dashboard — credentials-free cross-post helper.
 *
 * Three panels:
 *   1. "How am I doing?" — current subscriber count + weekly delta + sparkline
 *   2. "Log today's number" — manual weekly entry (30 seconds once she's
 *      already on Substack to publish)
 *   3. "Your Substack" — handle/URL config + quick link to publish & stats
 */

function formatDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-GB",
    { day: "numeric", month: "short", year: "numeric" }); }
  catch { return iso; }
}

// Simple inline SVG sparkline. We don't need a chart library for 28 values.
function Sparkline({ data, width = 280, height = 72 }) {
  if (!data || data.length < 2) {
    return <div style={{ color: "var(--elle-ink-faint)", fontSize: 12 }}>
      Log at least two days of numbers to see the trend.
    </div>;
  }
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  const step = width / (data.length - 1);
  const pts = data.map((v, i) => [
    i * step,
    height - 4 - ((v - min) / range) * (height - 8),
  ]);
  const path = pts.map(([x, y], i) => `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`).join(" ");
  const lastX = pts[pts.length - 1][0];
  const lastY = pts[pts.length - 1][1];
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}
         style={{ display: "block" }}>
      <path d={path} fill="none" stroke="var(--elle-amber)" strokeWidth="2"
            strokeLinecap="round" strokeLinejoin="round"/>
      <circle cx={lastX} cy={lastY} r="3" fill="var(--elle-amber)"/>
    </svg>
  );
}

export default function Substack() {
  const [cfg, setCfg] = React.useState(null);
  const [summary, setSummary] = React.useState(null);
  const [series, setSeries] = React.useState([]);
  const [issueMetrics, setIssueMetrics] = React.useState([]);
  const [countInput, setCountInput] = React.useState("");
  const [note, setNote] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [urlInput, setUrlInput] = React.useState("");

  // Reply drafter state.
  const [replyUrl, setReplyUrl] = React.useState("");
  const [replyPreview, setReplyPreview] = React.useState("");
  const [replyRegister, setReplyRegister] = React.useState("gabrielle");
  const [replyNote, setReplyNote] = React.useState("");
  const [replyDraft, setReplyDraft] = React.useState("");
  const [replyBusy, setReplyBusy] = React.useState(false);

  const draftReply = async () => {
    if (!replyUrl.trim() || replyPreview.trim().length < 10) {
      return toast.error("Paste the post URL and at least one sentence of context.");
    }
    setReplyBusy(true);
    try {
      const { data } = await api.post("/substack/draft-reply", {
        source_url: replyUrl,
        context_preview: replyPreview,
        register: replyRegister,
        note: replyNote || null,
      });
      setReplyDraft(data.draft || "");
      toast.success("Draft ready. Edit, then copy and open Substack.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Draft failed.");
    } finally {
      setReplyBusy(false);
    }
  };

  const copyReplyAndOpen = async () => {
    if (!replyDraft.trim()) return toast.error("No draft to copy.");
    try {
      try {
        await navigator.clipboard.writeText(replyDraft);
      } catch {
        const ta = document.createElement("textarea");
        ta.value = replyDraft;
        ta.style.position = "fixed"; ta.style.left = "-9999px";
        document.body.appendChild(ta);
        ta.select(); document.execCommand("copy"); document.body.removeChild(ta);
      }
      toast.success("Copied. Opening the post in a new tab…");
      if (replyUrl) window.open(replyUrl, "_blank", "noopener");
    } catch {
      toast.error("Couldn't copy.");
    }
  };

  const clearReply = () => {
    setReplyUrl(""); setReplyPreview(""); setReplyNote(""); setReplyDraft("");
  };

  const load = async () => {
    try {
      const [c, s, series_, im] = await Promise.all([
        api.get("/substack/config"),
        api.get("/substack/summary"),
        api.get("/substack/subscribers"),
        api.get("/substack/issue-metrics"),
      ]);
      setCfg(c.data);
      setSummary(s.data);
      setSeries(series_.data || []);
      setIssueMetrics(im.data || []);
      setUrlInput(c.data.substack_url || "");
    } catch {
      toast.error("Couldn't load Substack data.");
    }
  };
  React.useEffect(() => { load(); }, []);

  const logCount = async () => {
    const n = parseInt(countInput, 10);
    if (!n || n < 0 || n > 1_000_000) return toast.error("Enter a valid subscriber count.");
    setBusy(true);
    try {
      await api.post("/substack/subscribers", {
        subscribers: n,
        note: note || null,
      });
      toast.success(`Logged ${n} subscriber${n === 1 ? "" : "s"}.`);
      setCountInput("");
      setNote("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    } finally { setBusy(false); }
  };

  const saveConfig = async () => {
    const v = urlInput.trim();
    if (v && !/^https?:\/\//.test(v)) return toast.error("URL must start with https://");
    setBusy(true);
    try {
      await api.put("/substack/config", { substack_url: v });
      toast.success("Saved.");
      await load();
    } catch {
      toast.error("Save failed.");
    } finally { setBusy(false); }
  };

  if (!summary || !cfg) {
    return <p className="font-mono-elle text-sm" style={{ color: "var(--elle-ink-dim)" }}>Loading…</p>;
  }

  const delta = summary.weekly_delta || 0;
  const current = summary.current || 0;
  const hasData = summary.has_data;

  return (
    <div className="max-w-5xl" data-testid="substack-page">
      <div className="overline">Substack</div>
      <h1 className="font-display text-5xl mt-2" style={{ color: "var(--elle-ink)" }}>
        Growth, quietly tracked.
      </h1>
      <p className="text-sm mt-2 max-w-2xl" style={{ color: "var(--elle-ink-dim)" }}>
        Substack doesn't let anything outside itself post or read your stats. So
        we don't try. You paste from the shield, you read the numbers off
        Substack once a week, and the shield does the maths.
      </p>

      {/* ========= Panel 1: How am I doing? ========= */}
      <div className="grid md:grid-cols-2 gap-6 mt-10">
        <div className="elle-surface p-6 rounded-sm" data-testid="substack-growth-card">
          <div className="overline flex items-center gap-2"><BarChart3 size={12}/> Subscribers</div>
          <div className="flex items-end gap-4 mt-3">
            <div className="font-display text-6xl leading-none"
                 style={{ color: "var(--elle-ink)" }}
                 data-testid="substack-count">
              {hasData ? current : "—"}
            </div>
            {hasData && (
              <div className="flex items-center gap-1 pb-2 text-sm font-mono-elle"
                   style={{ color: delta > 0 ? "var(--elle-moss)"
                                              : delta < 0 ? "var(--elle-oxblood)"
                                                          : "var(--elle-ink-faint)" }}>
                {delta > 0 ? <TrendingUp size={14}/> : delta < 0 ? <TrendingDown size={14}/> : null}
                {delta > 0 ? `+${delta}` : delta}
                <span className="text-xs opacity-70 ml-1">this week</span>
              </div>
            )}
          </div>
          <div className="mt-5">
            <Sparkline data={summary.sparkline || []}/>
          </div>
          <div className="text-xs font-mono-elle mt-3" style={{ color: "var(--elle-ink-faint)" }}>
            {summary.last_logged_date
              ? `Last logged ${formatDate(summary.last_logged_date)}`
              : "No data yet — log today's count to start the chart."}
          </div>
        </div>

        {/* ========= Panel 2: Log today's number ========= */}
        <div className="elle-surface p-6 rounded-sm" data-testid="substack-log-panel">
          <div className="overline">Log today's number</div>
          <p className="text-sm mt-2" style={{ color: "var(--elle-ink-dim)" }}>
            Read the subscriber count from your Substack dashboard, type it here.
            Takes 15 seconds. One entry per day; save again to correct a typo.
          </p>
          <div className="mt-4">
            <label className="overline block mb-2">Subscribers today</label>
            <Input type="number" min="0" value={countInput}
                   onChange={e => setCountInput(e.target.value)}
                   placeholder="e.g. 47"
                   data-testid="substack-count-input"/>
          </div>
          <div className="mt-3">
            <label className="overline block mb-2">Note (optional)</label>
            <Input value={note}
                   onChange={e => setNote(e.target.value)}
                   placeholder="e.g. after Bluesky thread about advances"
                   data-testid="substack-note-input"/>
          </div>
          <Button onClick={logCount} disabled={busy}
                  className="btn-amber rounded-sm mt-4"
                  data-testid="substack-log-btn">
            <Save size={14} className="mr-2"/> Log this week's count
          </Button>
        </div>
      </div>

      {/* ========= Panel 3: Your Substack config ========= */}
      <div className="elle-surface p-6 rounded-sm mt-6" data-testid="substack-config-panel">
        <div className="overline flex items-center gap-2"><Link2 size={12}/> Your Substack</div>
        <div className="grid md:grid-cols-[1fr,auto] gap-3 mt-3 items-end">
          <div>
            <label className="overline block mb-2">Publication URL</label>
            <Input value={urlInput}
                   onChange={e => setUrlInput(e.target.value)}
                   placeholder="https://ellestuartauthor.substack.com"
                   data-testid="substack-url-input"/>
          </div>
          <Button onClick={saveConfig} disabled={busy}
                  variant="outline" className="btn-ghost rounded-sm"
                  data-testid="substack-url-save">
            Save
          </Button>
        </div>
        {cfg.substack_url && (
          <div className="flex flex-wrap gap-3 mt-4 text-sm font-mono-elle">
            <a href={cfg.substack_url} target="_blank" rel="noreferrer"
               className="underline hover:text-[var(--elle-amber)]"
               data-testid="substack-open-pub">
              <ExternalLink size={12} className="inline mr-1"/>
              Open publication
            </a>
            <a href={`${cfg.substack_url}/publish/posts`} target="_blank" rel="noreferrer"
               className="underline hover:text-[var(--elle-amber)]"
               data-testid="substack-open-editor">
              <ExternalLink size={12} className="inline mr-1"/>
              Open Substack editor
            </a>
            <a href={`${cfg.substack_url}/publish/stats`} target="_blank" rel="noreferrer"
               className="underline hover:text-[var(--elle-amber)]"
               data-testid="substack-open-stats">
              <ExternalLink size={12} className="inline mr-1"/>
              Open Substack stats
            </a>
          </div>
        )}
      </div>

      {/* ========= Reply drafter (comment under another writer's Substack post) ========= */}
      <div className="elle-surface p-6 rounded-sm mt-6" data-testid="substack-reply-drafter"
           style={{ borderLeft: "3px solid var(--elle-amber)" }}>
        <div className="overline flex items-center gap-2">
          <MessageCircle size={12}/> Draft a Substack reply
        </div>
        <p className="text-sm mt-2 max-w-2xl" style={{ color: "var(--elle-ink-dim)" }}>
          When you see a post worth commenting under — another writer's essay, a note,
          an interview — paste the URL and a sentence or two of what they wrote.
          The shield drafts a warm reply in your voice. You click "Copy &amp; open",
          paste on Substack, done.
        </p>

        <div className="grid md:grid-cols-2 gap-4 mt-5">
          <div>
            <label className="overline block mb-2">Post URL</label>
            <Input value={replyUrl}
                   onChange={e => setReplyUrl(e.target.value)}
                   placeholder="https://someone.substack.com/p/the-essay"
                   data-testid="reply-url-input"/>
          </div>
          <div>
            <label className="overline block mb-2">Voice</label>
            <select value={replyRegister}
                    onChange={e => setReplyRegister(e.target.value)}
                    data-testid="reply-register"
                    className="w-full rounded-sm px-3 py-2 text-sm"
                    style={{ background: "var(--elle-surface-2)",
                             border: "1px solid var(--elle-line)",
                             color: "var(--elle-ink)" }}>
              <option value="gabrielle">Gabrielle · sharp / analytical</option>
              <option value="elle">Elle · warm / literary</option>
            </select>
          </div>
        </div>

        <div className="mt-4">
          <label className="overline block mb-2">What they wrote (paste a snippet)</label>
          <textarea value={replyPreview}
                    onChange={e => setReplyPreview(e.target.value)}
                    rows={4}
                    data-testid="reply-preview-input"
                    placeholder="Paste one or two sentences from the post so the shield knows what to respond to."
                    className="w-full rounded-sm px-3 py-2 text-sm"
                    style={{ background: "var(--elle-surface-2)",
                             border: "1px solid var(--elle-line)",
                             color: "var(--elle-ink)",
                             fontFamily: "var(--elle-mono)" }}/>
        </div>

        <div className="mt-3">
          <label className="overline block mb-2">Tone note (optional)</label>
          <Input value={replyNote}
                 onChange={e => setReplyNote(e.target.value)}
                 placeholder='e.g. "more gentle" / "push back a little"'
                 data-testid="reply-note-input"/>
        </div>

        <div className="flex flex-wrap gap-2 mt-4">
          <Button onClick={draftReply} disabled={replyBusy}
                  className="btn-amber rounded-sm"
                  data-testid="reply-draft-btn">
            <Sparkles size={14} className="mr-2"/>
            {replyBusy ? "Drafting…" : replyDraft ? "Redraft" : "Draft reply"}
          </Button>
          {(replyDraft || replyUrl || replyPreview) && (
            <Button onClick={clearReply} variant="outline" className="btn-ghost rounded-sm"
                    data-testid="reply-clear-btn">
              Clear
            </Button>
          )}
        </div>

        {replyDraft && (
          <div className="mt-5" data-testid="reply-draft-result">
            <label className="overline block mb-2">Draft — edit freely before copying</label>
            <textarea value={replyDraft}
                      onChange={e => setReplyDraft(e.target.value)}
                      rows={5}
                      data-testid="reply-draft-textarea"
                      className="w-full rounded-sm px-3 py-2 text-sm"
                      style={{ background: "var(--elle-surface-2)",
                               border: "1px solid var(--elle-line)",
                               color: "var(--elle-ink)",
                               fontFamily: "Georgia, serif",
                               lineHeight: "1.6" }}/>
            <div className="mt-3 flex flex-wrap gap-3 items-center">
              <Button onClick={copyReplyAndOpen}
                      className="btn-amber rounded-sm"
                      data-testid="reply-copy-open-btn">
                <ClipboardCopy size={14} className="mr-2"/>
                Copy &amp; open post
              </Button>
              <span className="text-xs font-mono-elle" style={{ color: "var(--elle-ink-faint)" }}>
                Paste into the comment box on Substack and hit reply. ~10 seconds.
              </span>
            </div>
          </div>
        )}
      </div>

      {/* ========= Panel 4: Per-issue metrics log ========= */}
      <div className="mt-10">
        <h2 className="font-display text-3xl mb-2" style={{ color: "var(--elle-ink)" }}>
          Per-issue metrics
        </h2>
        <p className="text-sm" style={{ color: "var(--elle-ink-dim)" }}>
          After you send a newsletter and publish it to Substack, come back a
          week later and log how it did. Helps the shield work out which
          topics actually drive growth.
        </p>

        {issueMetrics.length === 0 ? (
          <div className="elle-surface p-6 rounded-sm mt-4 text-center">
            <p className="font-mono-elle text-sm" style={{ color: "var(--elle-ink-faint)" }}>
              Nothing logged yet. After your first Substack post, come back and add its numbers.
            </p>
          </div>
        ) : (
          <div className="elle-surface mt-4 rounded-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="overline text-left" style={{ color: "var(--elle-ink-dim)" }}>
                  <th className="p-3">Issue</th>
                  <th className="p-3">Opens</th>
                  <th className="p-3">Likes</th>
                  <th className="p-3">Comments</th>
                  <th className="p-3">Restacks</th>
                  <th className="p-3">Updated</th>
                </tr>
              </thead>
              <tbody>
                {issueMetrics.map(m => (
                  <tr key={m.issue_slug} className="border-t"
                      style={{ borderColor: "var(--elle-line)" }}>
                    <td className="p-3">
                      {m.substack_post_url ? (
                        <a href={m.substack_post_url} target="_blank" rel="noreferrer"
                           className="hover:underline"
                           style={{ color: "var(--elle-ink)" }}>
                          {m.issue_slug}
                        </a>
                      ) : (
                        <span style={{ color: "var(--elle-ink)" }}>{m.issue_slug}</span>
                      )}
                    </td>
                    <td className="p-3 font-mono-elle text-xs" style={{ color: "var(--elle-ink-dim)" }}>{m.opens ?? "—"}</td>
                    <td className="p-3 font-mono-elle text-xs" style={{ color: "var(--elle-ink-dim)" }}>{m.likes ?? "—"}</td>
                    <td className="p-3 font-mono-elle text-xs" style={{ color: "var(--elle-ink-dim)" }}>{m.comments ?? "—"}</td>
                    <td className="p-3 font-mono-elle text-xs" style={{ color: "var(--elle-ink-dim)" }}>{m.restacks ?? "—"}</td>
                    <td className="p-3 font-mono-elle text-xs" style={{ color: "var(--elle-ink-dim)" }}>{formatDate(m.updated_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ========= History table of subscriber log ========= */}
      {series && series.length > 0 && (
        <div className="mt-10">
          <h2 className="font-display text-3xl mb-2" style={{ color: "var(--elle-ink)" }}>
            Subscriber history
          </h2>
          <div className="elle-surface mt-4 rounded-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="overline text-left" style={{ color: "var(--elle-ink-dim)" }}>
                  <th className="p-3">Date</th>
                  <th className="p-3">Subscribers</th>
                  <th className="p-3">Note</th>
                </tr>
              </thead>
              <tbody>
                {[...series].reverse().map(row => (
                  <tr key={row.date} className="border-t"
                      style={{ borderColor: "var(--elle-line)" }}>
                    <td className="p-3 font-mono-elle text-xs" style={{ color: "var(--elle-ink-dim)" }}>{formatDate(row.date)}</td>
                    <td className="p-3" style={{ color: "var(--elle-ink)" }}>{row.subscribers}</td>
                    <td className="p-3 text-xs" style={{ color: "var(--elle-ink-faint)" }}>{row.note || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
